import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from '../bookings/util.js';
import { ORDER_STATUS } from './orders.js';

// Office: Stock > Orders. Each team's order from Nano Fibre storerooms and
// where it's got to; open ones by default, or everything from the last 60
// days. Click one to see the lines (ordered and picked).
const TONE_CLASS = { wait: 'status-on_the_way', ok: 'status-completed', done: 'status-not_started' };

export default function StockOrders() {
  const [show, setShow] = useState('open');
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api(`/stock/orders?status=${show}`).then((d) => { setOrders(d.orders); setError(null); }, (e) => setError(problem(e)));
  }, [show]);
  useEffect(() => { load(); }, [load]);

  const badge = (o) => <span className={`status ${TONE_CLASS[ORDER_STATUS[o.status].tone]}`}>{ORDER_STATUS[o.status].label}</span>;

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Orders</h1>
            <p className="muted">Each team orders its stock in the app and collects it from a Nano Fibre storeroom. One open order per team, which they can change until it’s collected. Nano Fibre’s storeroom picks it; the engineer, or the storeroom, confirms collection.</p>
          </div>
        </div>
      </section>
      <section className="card">
        <div className="ticket-views">
          <label className="small">Show{' '}
            <select value={show} onChange={(e) => setShow(e.target.value)} aria-label="Which orders">
              <option value="open">Open orders</option>
              <option value="all">Last 60 days</option>
            </select>
          </label>
          <button type="button" className="btn icon-only" aria-label="Refresh" onClick={load}><RefreshCw size={18} aria-hidden="true" /></button>
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!orders && !error && <p className="muted">Loading…</p>}
        {orders && !orders.length && <p className="muted">{show === 'open' ? 'No open orders.' : 'No orders in the last 60 days.'}</p>}
        {orders && orders.length > 0 && (
          <div className="table-scroll">
            <table className="table clickable">
              <thead><tr><th>Order</th><th>Team</th><th>Storeroom</th><th>Items</th><th>Ordered</th><th>Status</th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} onClick={() => setOpen(o)}>
                    <td><button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); setOpen(o); }}>{o.ref}</button>{o.nfukRef && <div className="small muted">{o.nfukRef}</div>}</td>
                    <td>{o.teamName}</td>
                    <td>{o.storeroomName}</td>
                    <td>{o.lines.reduce((n, l) => n + l.qty, 0)} <span className="small muted">({o.lines.length} line{o.lines.length === 1 ? '' : 's'})</span></td>
                    <td>{formatDateTime(o.orderedAt)}<div className="small muted">{o.orderedByName}{o.edits ? `, changed ${o.edits}×` : ''}</div></td>
                    <td>{badge(o)}{!o.sent && <div className="small muted">{o.pushError ? 'Not with Nano Fibre yet' : 'Sending…'}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal open={Boolean(open)} title={open ? `${open.ref}: ${open.teamName}` : ''} onClose={() => setOpen(null)}>
        {open && (
          <div className="stack">
            <p>{badge(open)} <span className="small muted">from {open.storeroomName}{open.nfukRef ? ` · Nano Fibre ${open.nfukRef}` : ''}</span></p>
            <dl className="stock-flag-rows small">
              <div><dt>Ordered</dt><dd>{formatDateTime(open.orderedAt)} by {open.orderedByName}</dd></div>
              {open.edits > 0 && <div><dt>Changed</dt><dd>{formatDateTime(open.editedAt)} by {open.editedByName} ({open.edits}×)</dd></div>}
              {open.pickedAt && <div><dt>Picked</dt><dd>{formatDateTime(open.pickedAt)}{open.pickedByName ? ` by ${open.pickedByName}` : ''}</dd></div>}
              {open.collectedAt && <div><dt>Collected</dt><dd>{formatDateTime(open.collectedAt)} by {open.collectedByName} ({open.collectedBy === 'storeroom' ? 'storeroom' : 'engineer'})</dd></div>}
              {open.cancelledAt && <div><dt>Cancelled</dt><dd>{formatDateTime(open.cancelledAt)} by {open.cancelledByName}</dd></div>}
            </dl>
            <table className="table">
              <thead><tr><th>Item</th><th>SKU</th><th className="num">Ordered</th><th className="num">Picked</th></tr></thead>
              <tbody>{open.lines.map((l) => <tr key={l.itemId}><td>{l.description}</td><td className="muted small">{l.sku}</td><td className="num"><strong>{l.qty}</strong></td><td className="num">{l.qtyPicked ?? '—'}</td></tr>)}</tbody>
            </table>
            {open.note && <p><strong>Note:</strong> {open.note}</p>}
            {open.pushError && !open.sent && <p className="notice-line notice-warn small">Nano Fibre hasn’t got the latest of this yet ({open.pushError}); it’s retried every minute.</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
